import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
} from '@angular/common/http';
import { Injectable } from '@angular/core';
import {
  catchError,
  EMPTY,
  from,
  map,
  Observable,
  of,
  switchMap,
  tap,
  throwError,
} from 'rxjs';
import { environment } from '../../../environments/environment';
import { TokenManagerService } from '../services/token-manager.service';
import { Organization, UserService } from '../services/user.service';
import { MsalService } from '@azure/msal-angular';
import { InteractionRequiredAuthError } from '@azure/msal-browser';
import { Router } from '@angular/router';
import { AuthErrorService } from '../services/auth-error.service';

@Injectable()
export class ApiInterceptor implements HttpInterceptor {
  constructor(
    private tokenManagerService: TokenManagerService,
    private authErrorService: AuthErrorService,
    private userService: UserService,
    private msalService: MsalService,
    private router: Router
  ) {}

  private apiBaseUrl = environment.apiConfig.uri;
  private environmentScopes = environment.apiConfig.scopes;
  private apimApiKey = environment['Ocp-Apim-Subscription-Key'];

  private acquireMsalToken(): Observable<string | undefined> {
    const account =
      this.msalService.instance.getActiveAccount() ??
      this.msalService.instance.getAllAccounts()[0];

    if (!account) {
      // Not logged in with MSAL
      return of(undefined);
    }

    return from(
      this.msalService.instance.acquireTokenSilent({
        scopes: this.environmentScopes,
        account,
      })
    ).pipe(
      map((res) => res.accessToken),
      tap((accessToken) => {
        if (accessToken) this.tokenManagerService.setAccessToken(accessToken);
      }),
      catchError((err) => {
        if (err instanceof InteractionRequiredAuthError) {
          // You can trigger interactive here, or let it bubble
          this.msalService.loginRedirect({
            scopes: this.environmentScopes,
            redirectStartPage: location.href,
          });
        }
        return of(undefined); // continue without token; server may 401
      })
    );
  }

  intercept(
    req: HttpRequest<any>,
    next: HttpHandler
  ): Observable<HttpEvent<any>> {
    if (!req.url.startsWith(this.apiBaseUrl)) {
      return next.handle(req);
    }

    const organization: Organization | null =
      this.userService.getOrganization();

    const isIntegrationEmployee: boolean =
      organization === 'integration-employee';

    const tokenVar = isIntegrationEmployee
      ? of(this.tokenManagerService.getAccessToken() || '')
      : this.acquireMsalToken();

    // Apply the headers
    return tokenVar.pipe(
      switchMap((token) => {
        const headers: Record<string, string> = {};

        if (token) {
          headers['Authorization'] = `Bearer ${token}`;
        }

        if (this.apimApiKey) {
          headers['Ocp-Apim-Subscription-Key'] = this.apimApiKey;
        }

        const authReq =
          Object.keys(headers).length > 0
            ? req.clone({ setHeaders: headers })
            : req;

        return next.handle(authReq);
      }),
      catchError((err: HttpErrorResponse) => {
        console.error(`HTTP ${err.status ?? '?'} on ${req.method} ${req.url}`);

        if (err.status === 401) {
          this.authErrorService.setErrorMessage(
            'Access token expired. Please log in again.'
          );
          const organizationId =
            this.userService.getUserInfo()?.organizationId ?? '';

          const tree = this.router.createUrlTree(['/logout'], {
            queryParams: {
              reason: 'Expired Access Token',
              organizationId,
            },
          });

          this.router.navigateByUrl(tree);

          // Stop the stream so callers don't also handle the 401
          return EMPTY;
        }
        return throwError(() => err);
      })
    );
  }
}
